import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle, AlertTriangle, Loader2, ChevronDown, ChevronUp, Clock, RefreshCw } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import { Button, Modal, useToast } from '@/components/ui'
import type { TimeEntry } from './TrackingTimeline.tsx'

// ─── Types ────────────────────────────────────────────────────────────────────

export type GroupKind = 'paperclip_module' | 'custom_module' | 'tanss_ticket'
export type BookingStatus =
  | 'preparing'
  | 'booking'
  | 'booked'
  | 'partially_failed'
  | 'report_pending'
  | 'completed'
export type EntryBookingStatus = 'booked' | 'failed' | 'skipped'

interface BookingEntryDetail {
  entryId: string
  displayText: string
  originalText: string
  durationMinutes: number
  entryType: string
  startTimeUnix: number
}

interface BookingGroup {
  key: string
  kind: GroupKind
  moduleName?: string
  tanssTicketId?: number
  entryIds: string[]
  entries: BookingEntryDetail[]
  totalMinutes: number
  labels: string[]
}

interface EntryResult {
  entryId: string
  ticketId: number | null
  supportId: number | null
  status: EntryBookingStatus
  error?: string
  fallbackReason?: string
}

interface GroupResult {
  key: string
  kind: GroupKind
  tanssTicketId: number | null
  ticketCreated: boolean
  ticketFallbackReason?: string
  entryResults: EntryResult[]
  success: boolean
  error?: string
}

interface Booking {
  id: string
  userId: string
  date: string
  status: BookingStatus
  groups: BookingGroup[] | null
  result: GroupResult[] | null
  errorMsg: string | null
  createdAt: string
  updatedAt: string
}

interface ParseError {
  entryId: string
  label: string
  reason: string
}

interface TanssUserConfig {
  userId: string
  tanssEmployeeId: number | null
  miscTicketId: number | null
  defaultCompanyId: number | null
}

// ─── API ──────────────────────────────────────────────────────────────────────

async function fetchTanssConfig(): Promise<TanssUserConfig> {
  const { data } = await apiClient.get<TanssUserConfig>('/paperclip/tracking/tanss-config')
  return data
}

async function fetchAllTanssConfigs(): Promise<TanssUserConfig[]> {
  const { data } = await apiClient.get<TanssUserConfig[]>('/paperclip/tracking/tanss-config/all')
  return Array.isArray(data) ? data : []
}

async function putTanssConfig(userId: string, cfg: Omit<TanssUserConfig, 'userId'>): Promise<TanssUserConfig> {
  const { data } = await apiClient.put<TanssUserConfig>(`/paperclip/tracking/tanss-config/${userId}`, cfg)
  return data
}

async function postCreateBooking(date: string): Promise<{
  booking: Booking
  parseErrors: ParseError[]
  entries: TimeEntry[]
}> {
  const { data } = await apiClient.post('/paperclip/tracking/book', { date })
  return data as { booking: Booking; parseErrors: ParseError[]; entries: TimeEntry[] }
}

async function postExecuteBooking(bookingId: string): Promise<{ booking: Booking }> {
  const { data } = await apiClient.post<{ booking: Booking }>(`/paperclip/tracking/book/${bookingId}/execute`, {})
  return data
}

async function fetchBookings(date: string): Promise<Booking[]> {
  const { data } = await apiClient.get<Booking[]>('/paperclip/tracking/bookings', { params: { date } })
  return Array.isArray(data) ? data : []
}

async function fetchBooked(date: string): Promise<boolean> {
  const { data } = await apiClient.get<{ booked: boolean }>('/paperclip/tracking/booked', { params: { date } })
  return data.booked
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatMinutes(minutes: number): string {
  if (minutes <= 0) return '—'
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h > 0) return `${h}h ${m}min`
  return `${m}min`
}

const KIND_LABELS: Record<GroupKind, string> = {
  paperclip_module: 'Paperclip-Modul',
  custom_module: 'Freies Modul',
  tanss_ticket: 'TANSS-Ticket',
}

const STATUS_LABELS: Record<BookingStatus, string> = {
  preparing: 'Vorschau',
  booking: 'Bucht…',
  booked: 'Gebucht',
  partially_failed: 'Teilweise fehlgeschlagen',
  report_pending: 'Bericht ausstehend',
  completed: 'Abgeschlossen',
}

// ─── Admin Config Panel ───────────────────────────────────────────────────────

interface AdminUser {
  id: string
  name: string
  email: string
  role: string
  isActive?: boolean
}

interface AdminConfigPanelProps {
  currentUserId: string
}

export function TanssAdminConfigPanel({ currentUserId }: AdminConfigPanelProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [editingUserId, setEditingUserId] = useState<string | null>(null)
  const [editValues, setEditValues] = useState<{ tanssEmployeeId: string; miscTicketId: string; defaultCompanyId: string }>({
    tanssEmployeeId: '', miscTicketId: '', defaultCompanyId: '',
  })

  const { data: users = [] } = useQuery<AdminUser[]>({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const { data } = await apiClient.get<AdminUser[]>('/users')
      return Array.isArray(data) ? data : []
    },
    staleTime: 60_000,
  })

  const { data: configs = [] } = useQuery({
    queryKey: ['tanss-configs-all'],
    queryFn: fetchAllTanssConfigs,
    staleTime: 30_000,
  })

  const saveMutation = useMutation({
    mutationFn: ({ userId, cfg }: { userId: string; cfg: Omit<TanssUserConfig, 'userId'> }) =>
      putTanssConfig(userId, cfg),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tanss-configs-all'] })
      void queryClient.invalidateQueries({ queryKey: ['tanss-config-own'] })
      toast({ title: 'TANSS-Konfiguration gespeichert', tone: 'success' })
      setEditingUserId(null)
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Fehler beim Speichern'
      toast({ title: 'Fehler', description: msg, tone: 'danger' })
    },
  })

  function getConfig(userId: string): TanssUserConfig {
    return configs.find(c => c.userId === userId) ?? {
      userId,
      tanssEmployeeId: null,
      miscTicketId: null,
      defaultCompanyId: null,
    }
  }

  function startEdit(userId: string) {
    const cfg = getConfig(userId)
    setEditValues({
      tanssEmployeeId: cfg.tanssEmployeeId != null ? String(cfg.tanssEmployeeId) : '',
      miscTicketId: cfg.miscTicketId != null ? String(cfg.miscTicketId) : '',
      defaultCompanyId: cfg.defaultCompanyId != null ? String(cfg.defaultCompanyId) : '',
    })
    setEditingUserId(userId)
  }

  function saveEdit() {
    if (!editingUserId) return
    saveMutation.mutate({
      userId: editingUserId,
      cfg: {
        tanssEmployeeId: editValues.tanssEmployeeId ? parseInt(editValues.tanssEmployeeId, 10) : null,
        miscTicketId: editValues.miscTicketId ? parseInt(editValues.miscTicketId, 10) : null,
        defaultCompanyId: editValues.defaultCompanyId ? parseInt(editValues.defaultCompanyId, 10) : null,
      },
    })
  }

  return (
    <div className="border-t pt-2 mt-2">
      <button
        type="button"
        className="flex w-full items-center justify-between px-1 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
        onClick={() => setOpen(o => !o)}
      >
        <span>TANSS-Abschluss (Admin)</span>
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>

      {open && (
        <div className="flex flex-col gap-2 mt-1">
          <p className="text-xs text-muted-foreground px-1">
            Konfiguriere TANSS-Mitarbeiter-ID, Sammelticket und Standard-Firma pro Benutzer.
          </p>
          <div className="flex flex-col gap-1.5 max-h-48 overflow-y-auto">
            {users.map(user => {
              const cfg = getConfig(user.id)
              const isEditing = editingUserId === user.id
              const isOwn = user.id === currentUserId
              return (
                <div key={user.id} className={`rounded border px-2 py-1.5 text-xs ${isOwn ? 'border-brand-200 bg-brand-50' : ''}`}>
                  <div className="flex items-center justify-between gap-1 mb-1">
                    <span className="font-medium truncate">
                      {user.name}
                      {isOwn && <span className="ml-1 text-brand-600">(ich)</span>}
                    </span>
                    {!isEditing && (
                      <button
                        type="button"
                        className="shrink-0 text-xs text-brand-600 hover:underline"
                        onClick={() => startEdit(user.id)}
                      >
                        Bearbeiten
                      </button>
                    )}
                  </div>
                  {isEditing ? (
                    <div className="flex flex-col gap-1">
                      <div className="flex gap-1 items-center">
                        <label className="w-28 shrink-0 text-muted-foreground">MA-ID:</label>
                        <input
                          type="number"
                          value={editValues.tanssEmployeeId}
                          onChange={e => setEditValues(v => ({ ...v, tanssEmployeeId: e.target.value }))}
                          className="flex-1 rounded border px-1.5 py-0.5 text-xs"
                          placeholder="z.B. 42"
                        />
                      </div>
                      <div className="flex gap-1 items-center">
                        <label className="w-28 shrink-0 text-muted-foreground">Sammelticket:</label>
                        <input
                          type="number"
                          value={editValues.miscTicketId}
                          onChange={e => setEditValues(v => ({ ...v, miscTicketId: e.target.value }))}
                          className="flex-1 rounded border px-1.5 py-0.5 text-xs"
                          placeholder="Ticket-ID"
                        />
                      </div>
                      <div className="flex gap-1 items-center">
                        <label className="w-28 shrink-0 text-muted-foreground">Firma-ID:</label>
                        <input
                          type="number"
                          value={editValues.defaultCompanyId}
                          onChange={e => setEditValues(v => ({ ...v, defaultCompanyId: e.target.value }))}
                          className="flex-1 rounded border px-1.5 py-0.5 text-xs"
                          placeholder="Interne Firma-ID"
                        />
                      </div>
                      <div className="flex gap-1 mt-0.5">
                        <button
                          type="button"
                          className="flex-1 rounded bg-brand-600 py-0.5 text-xs text-white hover:bg-brand-700 disabled:opacity-50"
                          onClick={saveEdit}
                          disabled={saveMutation.isPending}
                        >
                          Speichern
                        </button>
                        <button
                          type="button"
                          className="flex-1 rounded border py-0.5 text-xs hover:bg-gray-50"
                          onClick={() => setEditingUserId(null)}
                        >
                          Abbrechen
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2 text-muted-foreground flex-wrap">
                      <span>MA: <b>{cfg.tanssEmployeeId ?? '—'}</b></span>
                      <span>Ticket: <b>{cfg.miscTicketId ?? '—'}</b></span>
                      <span>Firma: <b>{cfg.defaultCompanyId ?? '—'}</b></span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Booking Preview Dialog ───────────────────────────────────────────────────

interface BookingPreviewDialogProps {
  open: boolean
  onClose: () => void
  date: string
  booking: Booking
  parseErrors: ParseError[]
  onConfirm: () => void
  isExecuting: boolean
}

function PlannedActionLabel({ group }: { group: BookingGroup }) {
  if (group.kind === 'tanss_ticket') {
    return <span className="text-blue-700">→ Ticket #{group.tanssTicketId}</span>
  }
  return <span className="text-purple-700">→ Neues TANSS-Ticket wird angelegt</span>
}

function GroupEntryList({ entries }: { entries: BookingEntryDetail[] }) {
  const [expanded, setExpanded] = useState(false)
  if (!entries.length) return null
  const visible = expanded ? entries : entries.slice(0, 2)
  return (
    <div className="mt-1.5 flex flex-col gap-0.5">
      {visible.map(e => (
        <div key={e.entryId} className="flex items-start justify-between gap-2 text-xs text-muted-foreground pl-2 border-l-2 border-gray-200">
          <span className="flex-1 min-w-0 line-clamp-2">{e.displayText}</span>
          <span className="shrink-0 font-mono">{formatMinutes(e.durationMinutes)}</span>
        </div>
      ))}
      {entries.length > 2 && (
        <button
          type="button"
          className="text-xs text-brand-600 hover:underline self-start mt-0.5"
          onClick={() => setExpanded(v => !v)}
        >
          {expanded ? 'Weniger anzeigen' : `${entries.length - 2} weitere…`}
        </button>
      )}
    </div>
  )
}

function BookingPreviewDialog({
  open, onClose, date, booking, parseErrors, onConfirm, isExecuting,
}: BookingPreviewDialogProps) {
  const groups = booking.groups ?? []
  const totalMinutes = groups.reduce((s, g) => s + g.totalMinutes, 0)
  const hasErrors = parseErrors.length > 0
  const canBook = !isExecuting && groups.length > 0 && !hasErrors

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`TANSS-Abschluss ${date}`}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={isExecuting}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onConfirm}
            disabled={!canBook}
            title={hasErrors ? 'Zuerst alle Fehler beheben oder Einträge aus der Timeline entfernen.' : undefined}
          >
            {isExecuting ? (
              <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Bucht…</>
            ) : (
              'Jetzt buchen'
            )}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {hasErrors && (
          <div className="rounded border border-red-300 bg-red-50 p-3">
            <p className="text-sm font-semibold text-red-800 mb-1">
              <AlertTriangle className="inline h-4 w-4 mr-1" />
              Abschluss gesperrt — {parseErrors.length} Eintrag{parseErrors.length !== 1 ? 'e' : ''} ohne gültigen Präfix:
            </p>
            <ul className="text-xs text-red-800 space-y-0.5 mb-1.5">
              {parseErrors.map(e => (
                <li key={e.entryId}>• <b>{e.label}</b>: {e.reason}</li>
              ))}
            </ul>
            <p className="text-xs text-red-700">
              Bitte Einträge in der Timeline korrigieren (Präfix <code>[ModulName]</code> oder <code>[TicketNr]</code> hinzufügen).
            </p>
          </div>
        )}

        {groups.length === 0 ? (
          <p className="text-sm text-muted-foreground italic text-center py-4">
            Keine buchbaren Gruppen gefunden.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-muted-foreground flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              {groups.length} Gruppe{groups.length !== 1 ? 'n' : ''} · {groups.reduce((s, g) => s + g.entries.length, 0)} Abschnitte · Gesamt: <b>{formatMinutes(totalMinutes)}</b>
            </p>
            {groups.map(group => (
              <div key={group.key} className="rounded border px-3 py-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate">
                      {group.moduleName ?? `Ticket #${group.tanssTicketId}`}
                    </p>
                    <div className="flex items-center gap-2 text-xs flex-wrap">
                      <span className={`rounded px-1.5 py-0.5 ${
                        group.kind === 'tanss_ticket'
                          ? 'bg-blue-100 text-blue-700'
                          : group.kind === 'paperclip_module'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {KIND_LABELS[group.kind]}
                      </span>
                      <span className="text-muted-foreground">{group.entries.length} Abschnitt{group.entries.length !== 1 ? 'e' : ''}</span>
                      <PlannedActionLabel group={group} />
                    </div>
                    <GroupEntryList entries={group.entries ?? []} />
                  </div>
                  <span className="text-sm font-semibold shrink-0 font-mono text-right">
                    {formatMinutes(group.totalMinutes)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-muted-foreground border-t pt-2">
          Jeder Abschnitt wird als separate TANSS-Leistung gebucht. Modul-Gruppen erhalten ein neu angelegtes Ticket.
        </p>
      </div>
    </Modal>
  )
}

// ─── Booking Result Dialog ────────────────────────────────────────────────────

interface BookingResultDialogProps {
  open: boolean
  onClose: () => void
  booking: Booking
  onRetry?: () => void
  isRetrying?: boolean
}

function EntryResultRow({ entry, result }: { entry: BookingEntryDetail; result: EntryResult | undefined }) {
  const status = result?.status
  return (
    <div className={`flex items-start gap-2 text-xs px-2 py-1 rounded ${
      status === 'booked' ? 'bg-green-50' :
      status === 'failed' ? 'bg-red-50' :
      status === 'skipped' ? 'bg-gray-50' :
      'bg-gray-50'
    }`}>
      <span className="mt-0.5 shrink-0">
        {status === 'booked' ? '✓' : status === 'failed' ? '✗' : '—'}
      </span>
      <div className="flex-1 min-w-0">
        <span className="truncate block">{entry.displayText}</span>
        {status === 'booked' && result?.fallbackReason && (
          <span className="text-yellow-700 text-xs">
            Sammelticket (#{result.ticketId}): {result.fallbackReason}
          </span>
        )}
        {status === 'booked' && result?.supportId && !result.fallbackReason && (
          <span className="text-green-700">Support #{result.supportId}</span>
        )}
        {status === 'failed' && result?.error && (
          <span className="text-red-700 break-words">{result.error}</span>
        )}
        {status === 'skipped' && (
          <span className="text-gray-500">{result?.error ?? 'Übersprungen'}</span>
        )}
      </div>
      <span className="shrink-0 font-mono text-muted-foreground">{formatMinutes(entry.durationMinutes)}</span>
    </div>
  )
}

function GroupResultBlock({
  group,
  result,
}: {
  group: BookingGroup
  result: GroupResult | undefined
}) {
  const [expanded, setExpanded] = useState(false)
  const entryResults = result?.entryResults ?? []
  const failedCount = entryResults.filter(e => e.status === 'failed').length
  const bookedCount = entryResults.filter(e => e.status === 'booked').length

  const headerCls = result?.success
    ? 'border-green-200 bg-green-50'
    : result
    ? 'border-yellow-200 bg-yellow-50'
    : 'border-gray-200'

  return (
    <div className={`rounded border text-sm ${headerCls}`}>
      <button
        type="button"
        className="w-full flex items-start justify-between gap-2 px-3 py-2 text-left"
        onClick={() => setExpanded(v => !v)}
      >
        <div className="flex flex-col gap-0.5 min-w-0 flex-1">
          <p className="font-medium truncate">
            {group.moduleName ?? `Ticket #${group.tanssTicketId}`}
          </p>
          <div className="flex gap-2 text-xs flex-wrap">
            <span className={`rounded px-1 py-0.5 ${
              group.kind === 'tanss_ticket' ? 'bg-blue-100 text-blue-700' :
              group.kind === 'paperclip_module' ? 'bg-purple-100 text-purple-700' :
              'bg-gray-100 text-gray-600'
            }`}>{KIND_LABELS[group.kind]}</span>
            {result?.tanssTicketId && (
              <span className="text-muted-foreground">
                {result.ticketCreated ? 'Neues Ticket' : 'Ticket'} #{result.tanssTicketId}
              </span>
            )}
            {result?.ticketFallbackReason && (
              <span className="text-yellow-700">⚠ {result.ticketFallbackReason} → Sammelticket</span>
            )}
            <span className={failedCount > 0 ? 'text-red-700' : 'text-green-700'}>
              {bookedCount}/{group.entries.length} gebucht
              {failedCount > 0 && ` · ${failedCount} fehlgeschlagen`}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <span className="font-mono text-xs text-muted-foreground">{formatMinutes(group.totalMinutes)}</span>
          {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </div>
      </button>

      {expanded && (
        <div className="flex flex-col gap-0.5 px-2 pb-2">
          {group.entries.map(entry => {
            const er = entryResults.find(e => e.entryId === entry.entryId)
            return <EntryResultRow key={entry.entryId} entry={entry} result={er} />
          })}
        </div>
      )}
    </div>
  )
}

function BookingResultDialog({ open, onClose, booking, onRetry, isRetrying }: BookingResultDialogProps) {
  const groups = booking.groups ?? []
  const results = booking.result ?? []
  const allOk = results.length > 0 && results.every(r => r.success)
  const anyFailed = results.some(r => !r.success)
  const canRetry = booking.status === 'partially_failed' && anyFailed

  const totalBooked = results.flatMap(r => r.entryResults).filter(e => e.status === 'booked').length
  const totalFailed = results.flatMap(r => r.entryResults).filter(e => e.status === 'failed').length
  const totalEntries = groups.reduce((s, g) => s + g.entries.length, 0)

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Buchungsergebnis"
      size="md"
      footer={
        <div className="flex justify-between gap-2">
          {canRetry && onRetry && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onRetry}
              disabled={isRetrying}
            >
              {isRetrying ? (
                <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />Wiederholt…</>
              ) : (
                <><RefreshCw className="mr-1 h-3.5 w-3.5" />Fehlgeschlagene wiederholen</>
              )}
            </Button>
          )}
          <Button variant="primary" size="sm" onClick={onClose} className="ml-auto">Schließen</Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <div className={`flex items-center gap-2 rounded border p-3 ${
          allOk ? 'border-green-300 bg-green-50' :
          anyFailed ? 'border-yellow-300 bg-yellow-50' :
          'border-gray-200'
        }`}>
          {allOk ? (
            <CheckCircle className="h-5 w-5 text-green-600 shrink-0" />
          ) : (
            <AlertTriangle className="h-5 w-5 text-yellow-600 shrink-0" />
          )}
          <div>
            <p className="text-sm font-medium">
              {allOk
                ? `Alle ${totalEntries} Leistungen erfolgreich gebucht!`
                : `${totalBooked} von ${totalEntries} Leistungen gebucht${totalFailed > 0 ? ` · ${totalFailed} fehlgeschlagen` : ''}`}
            </p>
            {canRetry && (
              <p className="text-xs text-yellow-700 mt-0.5">
                Fehlgeschlagene Leistungen können erneut gesendet werden — bereits gebuchte werden nicht doppelt übertragen.
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2 max-h-96 overflow-y-auto">
          {groups.map(group => {
            const result = results.find(r => r.key === group.key)
            return (
              <GroupResultBlock key={group.key} group={group} result={result} />
            )
          })}
        </div>
      </div>
    </Modal>
  )
}

// ─── Booking Badge ────────────────────────────────────────────────────────────

function BookingStatusBadge({ status }: { status: BookingStatus }) {
  const cls: Record<BookingStatus, string> = {
    preparing: 'bg-gray-100 text-gray-700',
    booking: 'bg-blue-100 text-blue-700',
    booked: 'bg-green-100 text-green-700',
    partially_failed: 'bg-yellow-100 text-yellow-700',
    report_pending: 'bg-purple-100 text-purple-700',
    completed: 'bg-green-100 text-green-700',
  }
  return (
    <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${cls[status]}`}>
      {STATUS_LABELS[status]}
    </span>
  )
}

// ─── Main TanssBookingButton ──────────────────────────────────────────────────

export interface TanssBookingButtonProps {
  date: string
  entryCount: number
}

export function TanssBookingButton({ date, entryCount }: TanssBookingButtonProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [showPreview, setShowPreview] = useState(false)
  const [showResult, setShowResult] = useState(false)
  const [booking, setBooking] = useState<Booking | null>(null)
  const [parseErrors, setParseErrors] = useState<ParseError[]>([])
  const [showHistory, setShowHistory] = useState(false)

  const { data: config } = useQuery({
    queryKey: ['tanss-config-own'],
    queryFn: fetchTanssConfig,
    staleTime: 60_000,
  })

  const { data: bookings = [] } = useQuery({
    queryKey: ['tanss-bookings', date],
    queryFn: () => fetchBookings(date),
    staleTime: 10_000,
    enabled: showHistory,
  })

  const { data: isBooked = false } = useQuery({
    queryKey: ['tanss-booked', date],
    queryFn: () => fetchBooked(date),
    staleTime: 30_000,
  })

  const configOk = !!config?.tanssEmployeeId

  const previewMutation = useMutation({
    mutationFn: () => postCreateBooking(date),
    onSuccess: ({ booking: b, parseErrors: errs }) => {
      setBooking(b)
      setParseErrors(errs)
      setShowPreview(true)
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Fehler beim Erstellen der Vorschau'
      toast({ title: 'Fehler', description: msg, tone: 'danger' })
    },
  })

  const executeMutation = useMutation({
    mutationFn: (bookingId: string) => postExecuteBooking(bookingId),
    onSuccess: ({ booking: b }) => {
      setBooking(b)
      setShowPreview(false)
      setShowResult(true)
      void queryClient.invalidateQueries({ queryKey: ['tanss-booked', date] })
      void queryClient.invalidateQueries({ queryKey: ['tanss-bookings', date] })
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Buchungsfehler'
      toast({ title: 'Buchungsfehler', description: msg, tone: 'danger' })
      setShowPreview(false)
    },
  })

  if (entryCount === 0) return null

  return (
    <>
      <div className="px-3 py-2 border-t">
        <div className="flex flex-col gap-1.5">
          {isBooked && (
            <div className="flex items-center gap-1 text-xs text-green-700">
              <CheckCircle className="h-3.5 w-3.5 shrink-0" />
              <span>In TANSS gebucht</span>
              <button
                type="button"
                className="ml-auto text-muted-foreground hover:text-foreground"
                onClick={() => setShowHistory(h => !h)}
              >
                {showHistory ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </button>
            </div>
          )}

          {showHistory && bookings.length > 0 && (
            <div className="flex flex-col gap-1">
              {bookings.map(b => (
                <div key={b.id} className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{new Date(b.createdAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span>
                  <BookingStatusBadge status={b.status} />
                </div>
              ))}
            </div>
          )}

          {!isBooked && (
            <>
              {!configOk && (
                <p className="text-xs text-yellow-700 bg-yellow-50 rounded px-2 py-1">
                  ⚠ TANSS nicht konfiguriert — Admin kontaktieren.
                </p>
              )}
              <Button
                variant="secondary"
                size="xs"
                className="w-full"
                disabled={!configOk || previewMutation.isPending}
                onClick={() => previewMutation.mutate()}
              >
                {previewMutation.isPending ? (
                  <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />Vorschau…</>
                ) : (
                  'In TANSS abschließen'
                )}
              </Button>
            </>
          )}
        </div>
      </div>

      {booking && (
        <>
          <BookingPreviewDialog
            open={showPreview}
            onClose={() => setShowPreview(false)}
            date={date}
            booking={booking}
            parseErrors={parseErrors}
            onConfirm={() => executeMutation.mutate(booking.id)}
            isExecuting={executeMutation.isPending}
          />
          <BookingResultDialog
            open={showResult}
            onClose={() => setShowResult(false)}
            booking={booking}
            onRetry={() => executeMutation.mutate(booking.id)}
            isRetrying={executeMutation.isPending}
          />
        </>
      )}
    </>
  )
}
